import classNames from 'classnames'
import { useResponsive } from 'store'
import { DefineComponentProps } from 'utils'
import { Teleport, Transition } from 'vue'
import { CloseIcon } from '../icons/CloseIcon'
import type { CustomDefineComponent } from '../types/components'
import './Popup.scss'

export type PopupProps = {
  isShowLogo?: boolean
  onClose?: () => void
  showClose?: boolean
  header?: (() => CustomDefineComponent) | (() => VNode)
  default?: (() => CustomDefineComponent) | (() => VNode)
  changeRouterAutoClose?: boolean
}

export const Popup = defineComponent<PopupProps>({
  props: DefineComponentProps<PopupProps>(['onClose', 'showClose', 'header', 'default', 'isShowLogo', 'changeRouterAutoClose']),
  setup(props) {
    const isVisible = ref(false)
    const { showClose = false } = toRefs(props)
    const responsive = useResponsive()
    const route = useRoute()
    const changeRouterAutoClose = computed(() => {
      if (props.changeRouterAutoClose === undefined) {
        return true
      }
      return props.changeRouterAutoClose
    })

    onBeforeMount(() => {
      setTimeout(() => {
        isVisible.value = true
      })
    })

    const onClose = () => {
      isVisible.value = false
      setTimeout(() => {
        props.onClose?.()
      }, 300)
    }

    watch(
      () => responsive.isMobile,
      (val) => {
        if (!val) {
          onClose()
        }
      },
    )

    watch(
      () => route.path,
      () => {
        if (changeRouterAutoClose.value) {
          onClose()
        }
      },
    )

    watch(
      () => isVisible.value,
      (val) => {
        if (val) {
          document.documentElement.style.overflow = 'hidden'
        } else {
          document.documentElement.style.overflow = 'initial'
        }
      },
      { immediate: true },
    )

    return () => (
      <Teleport to="body">
        <Transition name="fade-move-right-to-left">
          <div
            class={classNames('popup-layout fade-move-right-to-left-enter-active fade-move-right-to-left-leave-active', {
              'fade-move-right-to-left-enter': isVisible.value,
            })}
          >
            <div class="header-container">
              {props.header?.()}
              {showClose && (
                <div class="close-btn" onClick={onClose}>
                  <CloseIcon />
                </div>
              )}
            </div>
            <div class="popup-body">{props.default?.()}</div>
          </div>
        </Transition>
      </Teleport>
    )
  },
})
